import {
  BaseSupabaseService,
  SupabaseService,
  SupabaseServiceView,
} from "../../../server/supabase/base-supabase-service";

import type { Database } from "../../../__definitions__/database-definitions";
import type { Json } from "../../../__definitions__/type-definitions";
import type { UUID } from "../../../utils/uuid";
import type { SlackManifest } from "../../integrations/types";
import type {
  PostgrestMaybeSingleResponse,
  PostgrestResponse,
  PostgrestSingleResponse,
} from "@supabase/postgrest-js";

export type CustomEmoji = Database["public"]["Tables"]["custom_emoji"]["Row"];
export type SlackAlias = Database["public"]["Tables"]["slack_alias"]["Row"];
export type SlackAliasWithDefault = Database["public"]["Views"]["slack_alias_with_default"]["Row"];
type SlackAliasDefault = Database["public"]["Tables"]["slack_alias_default"]["Row"];

export class SupabaseSlackAliasWithDefaultService extends SupabaseServiceView<"slack_alias_with_default"> {
  table = "slack_alias_with_default" as const;

  async setDefault(alias_id: UUID | null): Promise<PostgrestResponse<SlackAliasWithDefault>> {
    if (alias_id)
      return this.log_errors(await this.client.rpc("slack_alias_set_default", { alias_id }));

    return this.log_errors(await this.client.rpc("slack_alias_remove_default"));
  }
}

export class SupabaseSlackAliasService extends SupabaseService<"slack_alias"> {
  table = "slack_alias" as const;
}

export class SupabaseSlackAliasDefaultService extends SupabaseService<"slack_alias_default"> {
  table = "slack_alias_default" as const;

  async getSingle(): Promise<PostgrestMaybeSingleResponse<SlackAliasDefault>> {
    return this.log_errors(await this.client.from(this.table).select().single());
  }
}

export class SupabaseSuperAdminSlackService extends BaseSupabaseService<"super_admin"> {
  async createSlackManifest(
    manifest: SlackManifest,
  ): Promise<
    PostgrestSingleResponse<
      Database["super_admin"]["Functions"]["create_slack_manifest"]["Returns"]
    >
  > {
    return await this.client.rpc("create_slack_manifest", {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      manifest: manifest as unknown as Json,
    });
  }

  async updateSlackManifest(
    manifest: SlackManifest,
    app_id: string,
  ): Promise<
    PostgrestSingleResponse<
      Database["super_admin"]["Functions"]["update_slack_manifest"]["Returns"]
    >
  > {
    return await this.client.rpc("update_slack_manifest", {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      manifest: manifest as unknown as Json,
      app_id,
    });
  }
}

export class SupabaseCustomEmojiService extends SupabaseService<"custom_emoji"> {
  table = "custom_emoji" as const;

  async getByName(
    account_id: UUID,
    name: string,
  ): Promise<PostgrestMaybeSingleResponse<CustomEmoji>> {
    return this.log_errors(
      await this.client
        .from(this.table)
        .select()
        .eq("account_id", account_id)
        .eq("emoji", name)
        .maybeSingle(),
    );
  }

  /** The emoji a Communication's body names, worked out from its design in the database. */
  async getForMoment(moment_id: UUID, account_id: UUID) {
    return this.log_errors(
      await this.client.rpc("moment_custom_emoji", {
        moment_id_: moment_id,
        account_id_: account_id,
      }),
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- moment_custom_emoji RPC is typed as Json; it returns { emoji, url } rows
    ) as unknown as PostgrestResponse<{ emoji: string; url: string }>;
  }
}

export class SupabaseSlackAppService extends SupabaseServiceView<"slack_app_config"> {
  table = "slack_app_config" as const;

  async videoEnabled(account_id: UUID) {
    const select = this.client.from(this.table).select("*").in("version", ["4", "5"]);
    return this.log_errors(
      ((await this.getAllFromSelection(account_id, select, { order: [] }))?.data?.length ?? 0) > 0,
    );
  }
}
